import { isDev } from './env';
import { reportTechStat } from './report';
import type { PageConfig } from '@/utils/type';
import { getImageIdFromUrl } from './urlParams';
import { devDetailPageConfigMock } from './detailPageConfigMock';

/**
 * 获取 userId
 */
export const getUserId = (() => {
    let userId = '';

    const fetchUserId = () => {
        if (isDev) {
            return '//A8A095A6043842EE843429B3742FDE859d34eb0687bc211d954ffa526139e1cb/E6BEFB35EE8749BAA5E3D7A795D0F0DEEC7D424979CC9C51DC6E08ACA799A7BA';
        }

        if (!window.appJsBridge) {
            console.error('获取 userId 失败; 未找到 appJsBridge');
            return '';
        }

        const res = window.appJsBridge.callApi('getOpenId');
        if (res && res.code === 0) {
            return res.data;
        }

        console.error('获取 userId 失败', res);
        return '';
    };

    return () => {
        if (!userId) {
            userId = fetchUserId();
        }
        return userId;
    };
})();

type AppInfo = {
    appName: string;
    appVersionCode: number;
    appVersionName: string;
    identify: string;
    packageName: string;
};
/**
 * 获取 app 信息
 */
export const getAppInfo = (() => {
    let appInfo: AppInfo | null = null;

    const fetchAppInfo = () => {
        if (isDev) {
            return {
                appName: '乐划锁屏',
                appVersionCode: 1130000,
                appVersionName: '11.3.0_expp',
                identify: 'pictorial',
                packageName: 'com.heytap.pictorial',
            };
        }

        if (!window.appJsBridge) {
            console.error('获取 app 信息失败; 未找到 appJsBridge');
            return null;
        }

        const res = window.appJsBridge.callApi('getAppInfo');
        if (res && res.code === 0) {
            return res.data as AppInfo;
        }

        console.error('获取 app 信息失败', res);
        return null;
    };

    return () => {
        if (!appInfo) {
            appInfo = fetchAppInfo();
        }
        return appInfo;
    };
})();

type DeviceInfo = {
    brand: string;
    model: string;
    romVersion: string;
    system: string;
    version: string;
    versionCode: number;
};
/**
 * 获取设备信息
 */
export const getDeviceInfo = (() => {
    let deviceInfo: DeviceInfo | null = null;

    const fetchDeviceInfo = () => {
        if (isDev) {
            return {
                brand: 'OnePlus',
                model: 'LE2121',
                romVersion: 'V17.1.0',
                system: 'android',
                version: '13',
                versionCode: 33,
            };
        }

        if (!window.appJsBridge) {
            console.error('获取设备信息失败; 未找到 appJsBridge');
            return null;
        }

        const res = window.appJsBridge.callApi('getDeviceInfo');
        if (res && res.code === 0) {
            return res.data as DeviceInfo;
        }

        console.error('获取设备信息失败', res);
        return null;
    };

    return () => {
        if (!deviceInfo) {
            deviceInfo = fetchDeviceInfo();
        }
        return deviceInfo;
    };
})();

type PictorialHeads = { gaid: string; [key: string]: string };
/**
 * 获取 PictorialHeads
 */
export const getPictorialHeads = (() => {
    let pictorialHeads: PictorialHeads | null = null;

    const fetchPictorialHeads = (): Promise<PictorialHeads | null> => {
        return new Promise((resolve) => {
            if (isDev) {
                return resolve({
                    gaid: '4c6376c9-4994-4394-a7ea-5dc0f0142bc0',
                    appInfo:
                        '{"app_browser":"1153000","app_games":"51300090","app_store":"150300"}',
                    screen: '2412#1080',
                    ProductBrand: 'OnePlus',
                    packageName: 'com.heytap.pictorial',
                    mobileName: 'LE2121',
                    AndroidVersion: 'LE2121_13.1.0.593(EX01)',
                    VersionCode: '1130000',
                    deviceCurrentTime: '2024-12-24 11:01:22',
                    COSVersionCode: '27',
                    COSVersion: 'V13.1.0',
                    Region: 'VN',
                });
            }

            if (!window.appJsBridge) {
                console.error('获取 PictorialHeads 失败; 未找到 appJsBridge');
                return resolve(null);
            }

            window.appJsBridge.callApi(
                'getPictorialHeads',
                (res: { code: number; data: string; message: string }) => {
                    if (res && res.code === 0) {
                        try {
                            return resolve(JSON.parse(res.data));
                        } catch (error) {
                            console.error('获取 PictorialHeads 失败', res);
                            return resolve(null);
                        }
                    }

                    console.error('获取 PictorialHeads 失败', res);
                    return resolve(null);
                },
            );
        });
    };

    return async () => {
        if (pictorialHeads) {
            return Promise.resolve(pictorialHeads);
        }
        return fetchPictorialHeads().then((data) => {
            pictorialHeads = data;
            return data;
        });
    };
})();

/**
 * 获取 userSessionData
 */
export const getUserSessionData = (() => {
    let userSessionData = '';

    const fetchUserSessionData = () => {
        if (isDev) {
            return '{"consents":[{"status":true,"templateId":"H1Vl5NidjWX","timestamp":1748252953000},{"status":true,"templateId":"H1Vl5NidjWX","timestamp":1748252956000}],"controllerId":"0406fea06d23beea572b1d1a6d850cfc548c08ba5b0cddbf475dc69bf2b131f6","language":"en","tcf":{"tcString":"CQSBNYAQSBNYAAFADBENBsEsAP_gAEPAAAYgGMwHQAFAAQAAyABwAEAAKgAWgAyABoADoAIoATAAoABSAC2AGGAQABAgCDAFyAOcAfoBGACmgHtAQsAp8BdAC8wGMgnLARAA4ACAAFQAMgAaABMACgAOcAfoBFgCMAHtAU-AvMBfQDTQJygAAAAA.YAAAAAAAAAAA","vendorsDisclosed":[58,231,50,793,128,185,91,45,845,755,131,253,10,333,278,129,97,52,493,671,280,69,762,76,16,80,82,534,42,28,744,32,25,1253]},"ccpa":null}';
        }

        if (!window.appJsBridge) {
            console.error('获取 userSessionData 失败; 未找到 appJsBridge');
            return '';
        }

        const res = window.appJsBridge.callApi('getUserSessionData');
        if (res && res.code === 0 && res.data) {
            return res.data;
        }

        reportTechStat({
            error_type: `获取 userSessionData 失败; 版本: ${'home-page'}`,
            error_msg: JSON.stringify(res),
        });
        console.error('获取 userSessionData 失败', res);
        return '';
    };

    return () => {
        if (!userSessionData) {
            userSessionData = fetchUserSessionData();
        }
        return userSessionData;
    };
})();

/**
 * 客户端上报方法
 * @param category 埋点大类
 * @param statId 埋点 id
 * @param data 上报数据
 */
export const uploadStat = (
    category: string,
    statId: string,
    data: { key: string; value: string }[],
) => {
    if (!window.appJsBridge) {
        console.error('上报埋点失败; 未找到 appJsBridge');
        return;
    }

    const res = window.appJsBridge.callApi('uploadStatEvent', {
        category,
        statId,
        data,
    });
    if (res && res.code === 0) return;

    console.error('上报埋点失败', res);
};

type Locale = { country: string; language: string };
/**
 * 获取国家和语言
 */
export const getLocale = (() => {
    let locale: Locale | null = null;

    const fetchLocale = () => {
        if (isDev) {
            return { country: 'VN', language: 'en' };
        }

        if (!window.appJsBridge) {
            console.error('获取国家和语言失败; 未找到 appJsBridge');
            return null;
        }

        const res = window.appJsBridge.callApi('getLocale');
        if (res && res.code === 0) {
            return res.data as Locale;
        }

        console.error('获取国家和语言失败', res);
        return null;
    };

    return () => {
        if (!locale) {
            locale = fetchLocale();
        }
        return locale;
    };
})();

interface JSBResponse<T = string> {
    code: number;
    data: T;
    message: string;
}

const safeCallJsb = <T = string>(
    apiName: string,
    params: Record<string, unknown> | ((res: JSBResponse<T>) => void) = {},
): JSBResponse<T> | null => {
    if (!window.appJsBridge) {
        console.error(`[JSB] 调用失败: ${apiName}; 未找到 appJsBridge`);
        return null;
    }

    try {
        return window.appJsBridge.callApi(apiName, params) as JSBResponse<T>;
    } catch (error) {
        console.error(`[JSB] 运行时异常: ${apiName}`, error);
        return null;
    }
};

/**
 * 获取页面配置（与详情页 `getPageCondition` 一致：getSelfDetailPageConfig + imageId）
 */
export const getPageCondition = (() => {
    let pageCondition: PageConfig | null = null;

    const fetchPageCondition = () => {
        if (isDev) {
            return devDetailPageConfigMock;
        }

        const res = safeCallJsb('getSelfDetailPageConfig', {
            imageId: getImageIdFromUrl(),
        });

        if (res && res.code === 0) {
            try {
                const data = JSON.parse(res.data as string);
                if (data.component) {
                    data.component = JSON.parse(data.component);
                }
                if (data.advertise) {
                    data.advertise = JSON.parse(data.advertise);
                }
                if (`${data.pageId}` === '0') {
                    return null;
                }
                data.pageId = `${data.pageId}`;
                return data as PageConfig;
            } catch (err) {
                console.error('获取页面配置失败', res, err);
                return null;
            }
        }

        if (res && res.code === 404) return null;
        console.error('获取页面配置失败', res);
        return null;
    };

    return () => {
        if (!pageCondition) {
            pageCondition = fetchPageCondition();
        }
        return pageCondition;
    };
})();
